import {Plate, PlateContent, usePlateEditor, PlateContainer, createPlateEditor} from "platejs/react";
import {defaultValue, markdownDefaultMaxLength, MarkType, NodeType} from "./utils";
import {basePlatePlugins, preserveAttributes} from "./plugins/base-plate-plugins";
import {StrikethroughPlugin, UnderlinePlugin} from "@platejs/basic-nodes/react";
import {basePlateComponentOverrides, staticPlateComponents} from "./plugins/base-plate-component-overrides";
import styles from "./styles/SlateEditor.module.scss";
import Toolbar, {ToolbarGroup} from "./toolbar/Toolbar";
import {MarkButton} from "./toolbar/buttons/MarkButton";
import {LinkButton} from "./toolbar/buttons/LinkButton";
import {UndoButton, RedoButton} from "./toolbar/buttons/HistoryButtons";
import {FontBackgroundColorPlugin, FontColorPlugin, FontSizePlugin} from "@platejs/basic-styles/react";
import {FontSizeWidget} from "./toolbar/buttons/FontSizeWidget";
import {TurnIntoDropdownMenu} from "./toolbar/buttons/TurnIntoDropdownWidget";
import {AlignDropdownMenu} from "./toolbar/buttons/AlignDropdownMenu";
import {TextAlignPlugin} from "@platejs/basic-styles/react";
import {ColorDropdownMenu} from "./toolbar/buttons/ColorDropdownMenu";
import {HighlightPlugin} from "@platejs/basic-nodes/react";
import {IndentButton} from "./toolbar/buttons/IndentButton";
import {IndentPlugin} from "@platejs/indent/react";
import {OutdentButton} from "./toolbar/buttons/OutdentButton";
import {EmojiDropdownMenu} from "./toolbar/buttons/emojis/EmojiDropdownMenu";
import {EmojiPlugin} from "@platejs/emoji/react";
import emojiMartData from '@emoji-mart/data';
import {ImagePlugin, PlaceholderPlugin} from "@platejs/media/react";
import {ImageUploadButton} from "./toolbar/buttons/ImageUploadButton";
import {TrailingBlockPlugin} from "platejs";
import FontAwesome from "../utilities/FontAwesome";
import {ColumnItemPlugin, ColumnPlugin} from "@platejs/layout/react";
import {RefObject, useImperativeHandle, useState} from "react";
import {serializeHtml} from "platejs/static";
import classNames from "classnames";
import {widthFromClassList} from "./elements/Columns";
import {IframePlugin} from "./plugins/IframePlugin";
import {DivPlugin} from "./plugins/DivPlugin";
import {FloatingToolbar} from "./toolbar/FloatingToolbar";
import useAsyncEffect from "../../hooks/useAsyncEffect";

const editorPlugins = [
    ...basePlatePlugins,
    UnderlinePlugin,
    StrikethroughPlugin,
    FontSizePlugin,
    FontColorPlugin,
    FontBackgroundColorPlugin,
    TextAlignPlugin,
    HighlightPlugin,
    IndentPlugin,
    // @ts-ignore
    EmojiPlugin.configure({options: {data: emojiMartData}}),
    ImagePlugin.configure({
        parsers: {
            html: {
                deserializer: {
                    rules: [
                        {
                            validNodeName: 'IMG',
                        },
                    ],
                    parse: ({element, type}) => ({
                        type,
                        url: element.getAttribute('src'),
                        alt: element.getAttribute('alt'),
                    }),
                    attributeNames: preserveAttributes,
                }
            }
        }
    }),
    PlaceholderPlugin,
    TrailingBlockPlugin.configure(() => ({
        options: {
            level: 0,
            type: 'p',
        },
    })),
    DivPlugin,
    ColumnPlugin.configure({
        parsers: {
            html: {
                deserializer: {
                    parse: ({element, editor}) => {
                        if (element.classList.contains("column")) {
                            return {
                                type: ColumnItemPlugin.key,
                                width: widthFromClassList(element.classList),
                                flowOpposite: element.classList.contains("flow-opposite"),
                            }
                        } else if (element.classList.contains("row")) {
                            let isColumnContainer = false;
                            Array.from(element.children).forEach(child => {
                                if (child.classList.contains("column")) {
                                    isColumnContainer = true;
                                }
                            })
                            if (isColumnContainer) {
                                return {type: ColumnPlugin.key}
                            }
                        }
                    },
                    rules: [{validClassName: 'column'}, {validClassName: 'row'}],
                },
            },
        },
    }),
    IframePlugin,
];

export async function serializeSlateToHtml(liveEditor, maxLength?) {
    // Get the current editor state (children) from the live editor
    // Load the content into the static, override-free editor instance.
    // This uses the simplified static components (<img> for image nodes)
    const staticSerializerEditor = createPlateEditor({
        components: staticPlateComponents,
        maxLength: Math.min(maxLength, markdownDefaultMaxLength),
        value: liveEditor.children,
        plugins: editorPlugins,
    });

    // Serialize the content using the static editor.
    let html = await serializeHtml(staticSerializerEditor, {
        stripClassNames: false,
        stripDataAttributes: true,
    });

    // stripDataAttributes only removes specific "data-" attributes. This removes all of them.
    html = html.replaceAll(/ data-(?:[^"]+)="[^"]+"/g, '')

    return stripSlateClassNames(html);
}

function stripSlateClassNames(html: string) {
    return html.replaceAll(
        / class="([^"]*)"/g,
        (match: string, className: string) => {
            const classesToKeep = className
                .split(/\s+/)
                .filter((cn) => !/^slate-/.test(cn));

            return classesToKeep.length === 0
                ? ''
                : ` class="${classesToKeep.join(' ')}"`;
        }
    );
}

export function HtmlEditor(props: {
    value?: string,
    maxLength?: number,
    defaultEditor: "html" | "wysiwyg",
    ref: RefObject<any>,
    editorClassName?: string,
}) {
    const {maxLength = markdownDefaultMaxLength} = props;

    const [editRawHtml, setEditRawHtml] = useState(props.defaultEditor === "html");

    /*
    Yes, we are duplicating state here. But it's better than the subpar performance we incur when serializing to html
    on every editor value change.
     */
    const [rawHtml, setRawHtml] = useState(props.value || "");

    const editor = usePlateEditor({
        components: staticPlateComponents,
        value: props.value || defaultValue,
        // prevent overriding default max length to a longer limit - default is more than enough and is in place
        // to prevent bogging down the system when converting markdown to HTML
        maxLength: Math.min(maxLength, markdownDefaultMaxLength),
        plugins: editorPlugins,
        override: {
            components: basePlateComponentOverrides,
        },
    })

    useAsyncEffect(async () => {
        if (editRawHtml) {
            setRawHtml(await serializeSlateToHtml(editor));
        }
    }, [editRawHtml])

    useImperativeHandle(props.ref, () => ({
        editor: editor,
    }), [editor])

    function forceUpdateState(html) {
        editor.tf.reset(); // important to fix issue of lag between value changing and editor adjusting selection, see https://github.com/ianstormtaylor/slate/issues/3575
        const slateValue = editor.api.html.deserialize({
            element: html,
            // preserve whitespaces, which include &nbsp; which may have been created excessively by legacy page editor
            collapseWhiteSpace: false,
        });
        // @ts-ignore
        editor.tf.setValue(slateValue);
    }

    const editorClassName = classNames({
        [styles.editor]: true,
        [props.editorClassName || ""]: props.editorClassName,
    })

    return <div className={`${styles.container} notranslate`}>
        <div style={{display: editRawHtml ? "block" : "none"}} className={editorClassName}>
            <textarea
                placeholder="Enter some HTML"
                value={rawHtml}
                rows={8}
                maxLength={maxLength}
                onChange={e => {
                    forceUpdateState(e.target.value);
                    setRawHtml(e.target.value);
                }}
            />
        </div>
        <div style={{display: editRawHtml ? "none" : "block"}}>
            <Plate editor={editor}>
                <div className={styles.content}>
                    <Toolbar>
                        <ToolbarGroup>
                            <UndoButton/>
                            <RedoButton/>
                        </ToolbarGroup>
                        <ToolbarGroup>
                            <TurnIntoDropdownMenu/>
                        </ToolbarGroup>
                        <ToolbarGroup>
                            <FontSizeWidget/>
                        </ToolbarGroup>
                        <ToolbarGroup>
                            <MarkButton format={MarkType.STRONG} tooltip={"Bold"}>
                                <FontAwesome prefix={"fas"} name={"fa-bold"}/>
                            </MarkButton>
                            <MarkButton format={MarkType.EMPHASIS} tooltip={"Italic"}>
                                <FontAwesome prefix={"fas"} name={"fa-italic"}/>
                            </MarkButton>
                            <MarkButton format={MarkType.UNDERLINE} tooltip={"Underline"}>
                                <FontAwesome prefix={"fas"} name={"fa-underline"}/>
                            </MarkButton>
                            <MarkButton format={MarkType.STRIKETHROUGH} tooltip={"Strikethrough"}>
                                <FontAwesome prefix={"fas"} name={"fa-strikethrough"}/>
                            </MarkButton>
                            <MarkButton format={MarkType.CODE} tooltip={"Code"}>
                                <FontAwesome prefix={"fas"} name={"fa-code"}/>
                            </MarkButton>
                            <ColorDropdownMenu nodeType={FontColorPlugin.key} tooltip={"Font Color"}/>
                            <ColorDropdownMenu nodeType={FontBackgroundColorPlugin.key} tooltip={"Background Color"}/>
                        </ToolbarGroup>
                        <ToolbarGroup>
                            <LinkButton showTooltip={true} />
                            <MarkButton format={MarkType.HIGHLIGHT} tooltip={"Highlight"}>
                                <FontAwesome prefix={"fas"} name={"fa-highlighter"}/>
                            </MarkButton>
                            <EmojiDropdownMenu/>
                            <ImageUploadButton/>
                        </ToolbarGroup>
                        <ToolbarGroup>
                            <AlignDropdownMenu showTooltip={true}/>
                            <OutdentButton/>
                            <IndentButton/>
                        </ToolbarGroup>
                    </Toolbar>
                    <FloatingToolbar>
                        <ToolbarGroup>
                            <TurnIntoDropdownMenu/>
                        </ToolbarGroup>
                        <ToolbarGroup>
                            <MarkButton format={MarkType.STRONG}>
                                <FontAwesome prefix={"fas"} name={"fa-bold"}/>
                            </MarkButton>
                            <MarkButton format={MarkType.EMPHASIS}>
                                <FontAwesome prefix={"fas"} name={"fa-italic"}/>
                            </MarkButton>
                            <MarkButton format={MarkType.UNDERLINE}>
                                <FontAwesome prefix={"fas"} name={"fa-underline"}/>
                            </MarkButton>
                            <MarkButton format={MarkType.STRIKETHROUGH}>
                                <FontAwesome prefix={"fas"} name={"fa-strikethrough"}/>
                            </MarkButton>
                        </ToolbarGroup>
                        <ToolbarGroup>
                            <LinkButton showTooltip={false}/>
                        </ToolbarGroup>
                        <ToolbarGroup>
                            <AlignDropdownMenu showTooltip={false} />
                        </ToolbarGroup>
                    </FloatingToolbar>
                    <PlateContainer>
                        <div className={classNames(editorClassName, "sb-scoped-html")}>
                            <PlateContent/>
                        </div>
                    </PlateContainer>
                </div>
            </Plate>
        </div>
        <>
            <button
                onClick={() => setEditRawHtml(v => !v)}>{editRawHtml ? "Use WYSIWYG Editor" : "Edit in Raw HTML"}</button>
        </>
    </div>
}
